import { useEffect, useState } from 'react'
import Head from 'next/head'
import Script from 'next/script'
import Link from 'next/link'
import { s3Static } from '../../../utils/constants'
import { contentDate } from '../../../utils/article-date-time'
import { getRequestJSON } from '../../../api/index/location-api'
import { getFitnessRateBenchmarks } from '../../../api/personal-trainer/personal-trainer-api'
import { ptRateBenchmarksSnapshot, hasBenchmarks, formatBenchmarkWindow, formatBenchmarkAsOf } from 'data/pt-rate-benchmarks'
import { ptMarketRates, ptMarketRatesVerifiedOn } from 'data/pt-market-rates'
import { useRouter } from 'next/router'
import { LoadingCover } from '../../../components/commons/LoadingCover'
import { AnimatePresence } from 'framer-motion'
import { SystemError } from '../../../components/error/SystemError'
import { PtCostCalculator } from '@/index/service-category/personal-trainer/PtCostCalculator'
import { PersonalTrainerHead } from '@/index/service-category/personal-trainer/PersonalTrainerHead'
import { DarkFaq } from '@/index/service-category/personal-trainer/DarkFaq'
import { ptRatesFaqs } from 'utils/faq'
import { ArticleByline } from '../../../components/commons/ArticleByline'
import { harveyFinn } from 'data/authors'
import { RequestProvider } from 'context/RequestContext'
import { openModal } from 'service/modal-service'

// When this page's content last actually changed. Bump it by hand when you
// edit the copy - not for layout/UX changes. Feeds dateModified and
// article:modified_time (see reviewedIso in utils/article-date-time).
const CONTENT_REVIEWED = '2026-09-25'

export async function getStaticProps() {
    const countryName = "Singapore"
    const { modifiedDate, month, year } = contentDate(CONTENT_REVIEWED);

    const allRequestCategorySettings = await getRequestJSON("Singapore")
    const requestCategorySettings = allRequestCategorySettings.filter(category => category.requestType === "Fitness Trainer")

    // Live platform rate aggregates, with the committed snapshot as the fallback so a
    // backend outage degrades the benchmark section instead of failing the build.
    let benchmarks = ptRateBenchmarksSnapshot
    try {
        const live = await getFitnessRateBenchmarks("Singapore")
        if (hasBenchmarks(live)) {
            benchmarks = live
        }
    } catch (error) {
        console.log("Rate benchmarks unavailable, using committed snapshot", error?.message)
    }

    // The endpoint still computes per-district medians, but they sit at 7-12 packages
    // each - too thin to publish, so the page shows a qualitative note instead. Strip
    // them here so those counts are not sitting in the page's JSON payload either;
    // anything shipped to the browser is effectively published.
    benchmarks = { ...benchmarks, byDistrict: [] }

  return {
      props: {
        requestCategorySettings,
        benchmarks,
        modifiedDate,
        year
      },
      // Next.js will attempt to re-generate the page:
      // - When a request comes in
      // - At most once every 10 seconds
      revalidate: 259200, // In seconds
  }
}

export default function Home(props) {
    const router = useRouter()
    const year = props.year
    const requestCategorySettings = props.requestCategorySettings;
    const benchmarks = props.benchmarks
    const showBenchmarks = hasBenchmarks(benchmarks)
    const benchmarkWindow = formatBenchmarkWindow(benchmarks)
    const benchmarkAsOf = formatBenchmarkAsOf(benchmarks)

    const publishedDate = "2025-01-07T07:30:46+00:00"

    // Hand-maintained review date. Bump this ONLY when the rate figures on this page
    // actually change - do not wire it back to the build clock. The page regenerates
    // every 3 days (see revalidate below), so deriving dateModified from build time
    // advertised a fresh update every 3 days with no content change, which is a
    // freshness signal search engines discount. See also nextReview / rateChangelog.
    const lastReviewed = "2026-09-20T00:00:00+08:00"
    const nextReview = "December 2026"

    // Shown on-page under the byline. Newest first, keep to the last three entries.
    const rateChangelog = [
        { date: "20 September 2026", note: "Added the named-provider rate comparison and monthly cost table; re-verified all market ranges." },
        { date: "5 September 2026", note: "Refreshed commercial gym and elite trainer ranges against advertised 2026 rates." },
        { date: "7 January 2025", note: "Guide first published." },
    ]

    const pageTitle = `Personal Trainer Rates Singapore: $80-$150 a Session [${year}]`
    const pageDescription = `How much does a personal trainer cost in Singapore? Most sessions cost $80-$150, with the wider market spanning $50-$250. Compare rates by trainer experience, training location, session format and package size.`

    const requestType = "Fitness Trainer"
    const [requestForm, setRequestForm] = useState(false)
    const [isQuickBook, setIsQuickBook] = useState(false)
    const [isCustomBook, setIsCustomBook] = useState(false)

    const [ isLoading, setIsLoading ] = useState(false)
    const [ isSystemError, setIsSystemError ] = useState(false);
    const [ errorContent, setErrorContent ] = useState({'header': "", 'body': ""})

    const [ formValues, setFormValues ] = useState({
        gender : "Male",
        age : 30,
        height : 170,
        weight : 60,
        type: "Freelancer",
        disease: "No",
        goal: "Lose Weight"
    })

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormValues({...formValues, [name]: value})
    }

    const calculateCost = (e) => {
        e.preventDefault()
        let bmi = formValues.weight/formValues.height/formValues.height*10000
        let ptCost = 60
        if(bmi > 30){
            if(formValues.age > 40){
                ptCost = ptCost + 40
            } else {
                ptCost = ptCost + 20
            }
        } else {
            if(bmi > 25 ){
                if(formValues.age > 40){
                    ptCost = ptCost + 20
                }
            }
            if(bmi < 18.5){
                if(formValues.age > 40){
                    ptCost = ptCost + 30
                } else {
                    ptCost = ptCost + 10
                }
            }
        }
        if(formValues.type === "Gym"){
            ptCost = ptCost + 30
        }
        if(formValues.disease === "Yes"){
            ptCost = ptCost + 30
        }
        if(formValues.goal == "Get Stage Ready Looks"){
            ptCost = ptCost + 30
        }
        setErrorContent({'header': "Estimated Cost", 'body': `Your BMI is ${bmi.toFixed(2)} and you should pay around SGD ${ptCost} for personal training.`});
        setIsSystemError(true);
    }

    useEffect(() => {
        if (router.query.requestForm === "true") {
            setRequestForm(true)
            openModal(setRequestForm)
        }
    }, [router])

    const openRequestForm = () => {
        window.scrollTo({ top: 0 });
        openModal(setRequestForm)
    }

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
        <meta charset="UTF-8"/>
        <meta httpEquiv="X-UA-Compatible" content="IE=edge"/>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="description" content={pageDescription}/>
        <meta name="keywords" content="personal trainer rates singapore, personal trainer cost singapore, personal training prices singapore, how much does a personal trainer cost"/>
        <meta name="author" content={harveyFinn.name}/>
        <meta name="google-site-verification" content="JAjj5vAc8Xxrh4idRAchZhYR6rwxWPAk3vxMdx-dwVQ" />
        <meta httpEquiv="content-language" content="en-sg" />
        <meta property="og:locale" content="en_SG" />
        <meta property="og:url" content="https://www.stringssg.com/sg/personal-trainer/personal-trainer-rates-singapore" />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={pageTitle}/>
        <meta property="og:description" content={pageDescription} />
        <meta property="article:publisher" content="https://www.facebook.com/StringsSG/" />
        <meta property="article:author" content={harveyFinn.name} />
        <meta property="article:published_time" content={publishedDate} />
        <meta property="article:modified_time" content={lastReviewed} />
        <meta property="og:image" content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/personal-trainer/personal-trainer.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/personal-trainer/personal-trainer.jpg" />
        <meta name="twitter:site" content="@stringssg"/>
        <meta name="twitter:url" content="https://www.stringssg.com/sg/personal-trainer/personal-trainer-rates-singapore"/>
        <meta name="twitter:title" content={pageTitle}/>
        <meta name="twitter:description" content={pageDescription}/>
        <link rel="icon" href="/favicon.ico" />
        <link rel="canonical" href="https://www.stringssg.com/sg/personal-trainer/personal-trainer-rates-singapore" />
        <script type="application/ld+json"
          dangerouslySetInnerHTML={{ __html:
            JSON.stringify({
              "@context": "https://schema.org",
              "@graph": [
                {
                  "@type": "Article",
                  "mainEntityOfPage": { "@type": "WebPage", "@id": "https://www.stringssg.com/sg/personal-trainer/personal-trainer-rates-singapore" },
                  "headline": pageTitle,
                  "description": pageDescription,
                  "image": ["https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/personal-trainer/personal-trainer.jpg"],
                  "author": {
                    "@type": "Person",
                    "name": harveyFinn.name,
                    "email": harveyFinn.email,
                    "jobTitle": harveyFinn.jobTitle,
                    "knowsAbout": harveyFinn.knowsAbout,
                    "worksFor": { "@type": "Organization", "@id": "https://www.stringssg.com/#organization", "name": "StringsSG", "url": "https://www.stringssg.com" }
                  },
                  "reviewedBy": { "@type": "Organization", "name": "StringsSG Training Team", "url": "https://www.stringssg.com" },
                  "publisher": {
                    "@type": "Organization",
                    "name": "StringsSG",
                    "url": "https://www.stringssg.com",
                    "logo": { "@type": "ImageObject", "url": "https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/logo.png" }
                  },
                  "about": { "@type": "Thing", "name": "Personal trainer rates in Singapore" },
                  "datePublished": publishedDate,
                  "dateModified": lastReviewed
                },
                {
                  "@type": "Service",
                  "name": "Personal training in Singapore",
                  "serviceType": "Personal training",
                  "areaServed": { "@type": "Country", "name": "Singapore" },
                  "provider": { "@type": "Organization", "name": "StringsSG", "url": "https://www.stringssg.com" },
                  "offers": {
                    "@type": "AggregateOffer",
                    "priceCurrency": "SGD",
                    "lowPrice": "50",
                    "highPrice": "250",
                    "unitText": "per session",
                    "url": "https://www.stringssg.com/sg/personal-trainer/personal-trainer-rates-singapore"
                  }
                },
                {
                  "@type": "ProfessionalService",
                  "name": "StringsSG Personal Training Singapore",
                  "priceRange": "S$84.00 - S$110.00",
                  "url": "https://www.stringssg.com/sg/personal-trainer/personal-trainer-rates-singapore",
                  "sameAs": ["https://www.facebook.com/StringsSG", "https://www.linkedin.com/company/stringssg/"],
                  "logo": "https://stringssg-assets.s3.ap-southeast-1.amazonaws.com/static/logo.png",
                  "image": "https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/personal-trainer/personal-trainer.jpg",
                  "address": { "@type": "PostalAddress", "streetAddress": "60 Paya Lebar Road #06-06 Paya Lebar Square", "addressLocality": "Singapore", "postalCode": "409051", "addressCountry": "SG" },
                  "areaServed": { "@type": "Country", "name": "Singapore" },
                  "telephone": "+65 6680 3926"
                },
                {
                  "@type": "BreadcrumbList",
                  "itemListElement": [
                    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.stringssg.com" },
                    { "@type": "ListItem", "position": 2, "name": "Personal Trainer", "item": "https://www.stringssg.com/sg/personal-trainer" },
                    { "@type": "ListItem", "position": 3, "name": "Personal Trainer Rates", "item": "https://www.stringssg.com/sg/personal-trainer/personal-trainer-rates-singapore" }
                  ]
                },
                // Only described as a dataset when real aggregates are actually present.
                ...(showBenchmarks ? [{
                  "@type": "Dataset",
                  "name": "StringsSG personal training rate benchmarks, Singapore",
                  "description": `Median and quartile per-session personal training rates paid across ${benchmarks.sampleSize} separately priced personal training packages${benchmarks.totalSessions > 0 ? `, covering ${benchmarks.totalSessions} sessions,` : ''} booked on the StringsSG platform in Singapore, broken down by package size.`,
                  "url": "https://www.stringssg.com/sg/personal-trainer/personal-trainer-rates-singapore#platform-data",
                  "temporalCoverage": `${benchmarks.windowStart}/${benchmarks.windowEnd}`,
                  "spatialCoverage": { "@type": "Country", "name": "Singapore" },
                  "creator": { "@type": "Organization", "name": "StringsSG", "url": "https://www.stringssg.com" },
                  "license": "https://creativecommons.org/licenses/by/4.0/",
                  "isAccessibleForFree": true,
                  "variableMeasured": "Personal training rate per session (SGD)"
                }] : [])
              ]
            })
          }}
        />
        </Head>
        <div className="bg-slate-900">
            <RequestProvider>
                <PersonalTrainerHead
                    heroImage={s3Static + "personal-trainer/personal-trainer.jpg"}
                    title="Personal Trainer Rates In Singapore"
                    heading={`Personal Trainer Rates in Singapore ${year}: $80-$150 a Session`}
                    heroCopy="What personal training actually costs in Singapore, compared across gyms, studios and independent trainers."
                    setIsQuickBook={setIsQuickBook}
                    openRequestForm={openRequestForm}
                    requestForm={requestForm}
                    setRequestForm={setRequestForm}
                    isQuickBook={isQuickBook}
                    isCustomBook={isCustomBook}
                    requestType={requestType}
                    requestCategorySettings={requestCategorySettings}
                />
            </RequestProvider>
            <div className='max-w-5xl mx-auto py-10 px-4 leading-relaxed space-y-4 text-white/70'>
                {/* <h2 className='text-2xl md:text-4xl text-center text-white font-extrabold py-5'>
                    Personal Trainer Rates In Singapore
                </h2> */}
                <ArticleByline
                    author={harveyFinn}
                    publishedDate={publishedDate}
                    modifiedDate={lastReviewed}
                    variant="header"
                />

                {/* Verified-and-dated beats an auto-incrementing timestamp: this states when
                    the figures were actually re-checked and what changed. */}
                <div className="bg-white/3 border border-white/10 rounded-xl p-4 my-5">
                    <p className="text-xs text-white/50">
                        <span className="font-semibold text-white/70">Rates last verified {new Date(lastReviewed).toLocaleDateString('en-SG', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                        {' '}· next review {nextReview}
                    </p>
                    <ul className="mt-2 space-y-1">
                        {rateChangelog.map((entry) => (
                            <li key={entry.date} className="text-xs text-white/40 leading-relaxed">
                                <span className="text-white/55">{entry.date}</span> — {entry.note}
                            </li>
                        ))}
                    </ul>
                </div>

                <h2 className='text-xl font-semibold text-white/90'>How much does a personal trainer cost in Singapore?</h2>
                <p>
                    A personal trainer in Singapore costs roughly <strong className="text-white/90">$50 to $250 per session</strong>, and most qualified,
                    experienced trainers sit between <strong className="text-white/90">$80 and $150</strong>. What you pay comes down to five things: the
                    trainer&apos;s experience and certifications, where you train, whether you train alone or with a partner, the time slot you book, and how many
                    sessions you buy at once.
                </p>

                {/* TL;DR - answer-first summary */}
                <div className="bg-white/5 border border-white/10 rounded-2xl p-5 my-6">
                    <h3 className="text-sm font-bold uppercase tracking-wide text-orange-400 mb-3">TL;DR</h3>
                    <ul className="space-y-1 text-white/60 pl-2">
                        <li className="list-disc">Personal training in Singapore costs roughly <strong className="text-white/80">$50 to $250 per session</strong>; most experienced trainers charge <strong className="text-white/80">$80 to $150</strong>.</li>
                        <li className="list-disc"> Newly certified trainers sit at the low end, around <strong className="text-white/80">$70 to $90</strong>. Elite and specialist trainers occupy the top, from <strong className="text-white/80">$180</strong> upward.</li>
                        <li className="list-disc"> Commercial gym trainers commonly charge <strong className="text-white/80">$140 to $200</strong> per session, and usually require a paid membership on top.</li>
                        <li className="list-disc"> Buying a larger package typically lowers your per-session rate. Training with a partner can cut each person&apos;s cost by around 30%.</li>
                        <li className="list-disc"> No official body in Singapore sets or publishes personal training rates, so treat every figure here as a market snapshot, not a fixed tariff.</li>
                    </ul>
                </div>

                {/* Methodology / sourcing */}
                <div className="bg-white/3 border-l-4 border-white/20 rounded-r-lg p-4 my-6">
                    <h3 className="text-sm font-semibold text-white/70 mb-2">How we compiled these figures</h3>
                    <p className="text-sm text-white/50 leading-relaxed">
                        The ranges in this guide are compiled from two sources: personal training bookings and enquiries made through the StringsSG platform
                        {showBenchmarks && benchmarkWindow ? ` (${benchmarks.sampleSize.toLocaleString('en-SG')} bookings between ${benchmarkWindow})` : ''}, and
                        rates publicly advertised by Singapore trainers, studios and commercial gyms, each checked against the provider&apos;s own page and dated in
                        the comparison table below. There is no official body in Singapore that sets or publishes
                        personal training prices, so these figures describe what the market currently charges rather than a regulated rate. Platform figures reflect
                        activity on StringsSG and are not a nationally representative survey. Prices shown are per session before any promotional discount, and
                        individual quotes will vary.
                    </p>
                </div>

                {/* Table of contents */}
                <nav className="bg-white/3 border border-white/10 rounded-2xl p-5 my-6" aria-label="Table of contents">
                    <h3 className="text-sm font-bold uppercase tracking-wide text-white/70 mb-3">On this page</h3>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-4 text-sm">
                        <li><a href="#price-snapshot" className="text-orange-400/80 hover:text-orange-400">Price snapshot</a></li>
                        {showBenchmarks && (
                            <li><a href="#platform-data" className="text-orange-400/80 hover:text-orange-400">What people actually pay</a></li>
                        )}
                        <li><a href="#provider-rates" className="text-orange-400/80 hover:text-orange-400">Rates by named provider</a></li>
                        <li><a href="#monthly-cost" className="text-orange-400/80 hover:text-orange-400">Cost per month</a></li>
                        <li><a href="#cost-calculator" className="text-orange-400/80 hover:text-orange-400">Cost calculator</a></li>
                        <li><a href="#by-experience" className="text-orange-400/80 hover:text-orange-400">Rates by trainer experience</a></li>
                        <li><a href="#by-location" className="text-orange-400/80 hover:text-orange-400">Rates by training location</a></li>
                        <li><a href="#by-goal" className="text-orange-400/80 hover:text-orange-400">Rates by training goal</a></li>
                        <li><a href="#stringssg-prices" className="text-orange-400/80 hover:text-orange-400">StringsSG prices</a></li>
                        <li><a href="#faqs" className="text-orange-400/80 hover:text-orange-400">FAQs</a></li>
                    </ul>
                </nav>
                {/* Price snapshot table */}
                <div id="price-snapshot" className="scroll-mt-20 my-8">
                    <h2 className="text-xl md:text-2xl font-bold text-white mb-4">Personal trainer prices in Singapore at a glance</h2>
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse text-sm text-white/60">
                            <thead className="bg-white/6">
                                <tr>
                                    <th className="border border-white/10 p-3 text-left text-orange-400">Training option</th>
                                    <th className="border border-white/10 p-3 text-right text-orange-400">Typical price</th>
                                    <th className="border border-white/10 p-3 text-left text-orange-400">Gym membership</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className="border border-white/10 p-3">1-on-1 with a freelance trainer</td>
                                    <td className="border border-white/10 p-3 text-right font-semibold text-white/80">$80 - $150 / session</td>
                                    <td className="border border-white/10 p-3">Not required</td>
                                </tr>
                                <tr className="bg-white/2">
                                    <td className="border border-white/10 p-3">Buddy training (per person)</td>
                                    <td className="border border-white/10 p-3 text-right font-semibold text-white/80">$55 - $105 / person</td>
                                    <td className="border border-white/10 p-3">Not required</td>
                                </tr>
                                <tr>
                                    <td className="border border-white/10 p-3">Home or condo gym training</td>
                                    <td className="border border-white/10 p-3 text-right font-semibold text-white/80">$100 - $140 / session</td>
                                    <td className="border border-white/10 p-3">Not required</td>
                                </tr>
                                <tr className="bg-white/2">
                                    <td className="border border-white/10 p-3">Commercial gym trainer</td>
                                    <td className="border border-white/10 p-3 text-right font-semibold text-white/80">$140 - $200 / session</td>
                                    <td className="border border-white/10 p-3 text-amber-400/80">Usually required</td>
                                </tr>
                                <tr>
                                    <td className="border border-white/10 p-3">Elite or specialist trainer</td>
                                    <td className="border border-white/10 p-3 text-right font-semibold text-white/80">$180 - $250 / session</td>
                                    <td className="border border-white/10 p-3">Varies</td>
                                </tr>
                                <tr className="bg-white/2">
                                    <td className="border border-white/10 p-3">Online personal training</td>
                                    <td className="border border-white/10 p-3 text-right font-semibold text-white/80">$50 - $120 / session</td>
                                    <td className="border border-white/10 p-3">No</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* First-party platform data. Renders only when real aggregates are present -
                    see data/pt-rate-benchmarks.js. This is the section nobody else can publish. */}
                {showBenchmarks && (
                    <div id="platform-data" className="scroll-mt-20 my-8">
                        <h2 className="text-xl md:text-2xl font-bold text-white mb-4">What people actually pay: StringsSG booking data</h2>
                        {/* Volume and sample are deliberately two sentences. The estimate describes
                            the business; the sample describes what the medians are computed from.
                            Conflating them would make the methodology note below untrue. */}
                        {benchmarks.estimatedTotalPackages > 0 && (
                            <p className="text-white/70 leading-relaxed">
                                StringsSG arranged an estimated{' '}
                                <strong className="text-white/90">{benchmarks.estimatedTotalPackages.toLocaleString('en-SG')}+ personal training packages</strong>
                                {' '}between {benchmarkWindow}, counting those closed manually outside our booking system. The rates below are drawn from the{' '}
                                <strong className="text-white/90">{benchmarks.sampleSize.toLocaleString('en-SG')}</strong> of those that carry a complete pricing
                                record, so every figure here traces back to a booking we can check.
                            </p>
                        )}

                        <p className="text-white/70 leading-relaxed">
                            Every other figure on this page is an advertised rate. These are transacted ones. Across{' '}
                            {benchmarks.totalSessions > 0 ? (
                                <><strong className="text-white/90">{benchmarks.totalSessions.toLocaleString('en-SG')} personal training sessions</strong> booked
                                on StringsSG</>
                            ) : (
                                <><strong className="text-white/90">{benchmarks.sampleSize.toLocaleString('en-SG')} priced personal training packages</strong>
                                {' '}booked on StringsSG</>
                            )}
                            {' '}between {benchmarkWindow}, the median rate paid was{' '}
                            <strong className="text-white/90">${benchmarks.medianPerSession} per session</strong>
                            {benchmarks.p25PerSession && benchmarks.p75PerSession && (
                                <>, and half of all packages were priced between <strong className="text-white/90">${benchmarks.p25PerSession}</strong> and{' '}
                                <strong className="text-white/90">${benchmarks.p75PerSession}</strong> a session</>
                            )}.
                        </p>

                        {/* Without this, the transacted median reads as though it contradicts the
                            market range quoted at the top of the page. It does not - they measure
                            different populations, and saying so is more useful than hiding either. */}
                        <p className="text-white/60 leading-relaxed text-sm mt-3">
                            These figures sit below the $80 to $150 market range quoted at the top of this page, and that gap is the point rather than a
                            contradiction. StringsSG is a marketplace for independent trainers, who carry no gym overheads, so our bookings cluster at the
                            affordable end of the market. The wider range includes commercial gym trainers at $140 to $200 a session, which we do not broker.
                            Package discounts pull the per-session figure down further, since most bookings here are multi-session packages rather than one-off
                            sessions.
                        </p>

                        {[
                            // District medians render in the "rates by Singapore district" section
                            // below, not here - this page already uses "location" to mean home vs
                            // condo gym vs commercial gym, which is a different cut entirely.
                            { rows: benchmarks.byPackageSize, heading: "By package size", col: "Package" },
                            { rows: benchmarks.byTier, heading: "By trainer tier", col: "Tier" },
                        ].filter((group) => group.rows?.length > 0).map((group) => (
                            <div key={group.heading} className="my-6">
                                <h3 className="text-base font-semibold text-white/80 mb-3">{group.heading}</h3>
                                <div className="overflow-x-auto">
                                    <table className="w-full border-collapse text-sm text-white/60">
                                        <thead className="bg-white/6">
                                            <tr>
                                                <th className="border border-white/10 p-3 text-left text-orange-400">{group.col}</th>
                                                <th className="border border-white/10 p-3 text-right text-orange-400">Median</th>
                                                <th className="border border-white/10 p-3 text-right text-orange-400">Middle 50%</th>
                                                <th className="border border-white/10 p-3 text-right text-orange-400">Bookings</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {group.rows.map((row, i) => (
                                                <tr key={row.label} className={i % 2 ? "bg-white/2" : ""}>
                                                    <td className="border border-white/10 p-3">{row.label}</td>
                                                    <td className="border border-white/10 p-3 text-right font-semibold text-white/80">${row.median}</td>
                                                    <td className="border border-white/10 p-3 text-right">${row.p25} - ${row.p75}</td>
                                                    <td className="border border-white/10 p-3 text-right text-white/40">{row.n?.toLocaleString('en-SG')}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ))}

                        {benchmarks.buddy && (
                            <p className="text-white/70 leading-relaxed my-4">
                                Training with a partner costs less per person. Across{' '}
                                <strong className="text-white/90">{benchmarks.buddy.n.toLocaleString('en-SG')} buddy bookings</strong> in the same window, the
                                median was <strong className="text-white/90">${benchmarks.buddy.median} per person, per session</strong>. Buddy rates are kept out
                                of the headline figures above, because a shared rate is not comparable to a 1-on-1 one.
                            </p>
                        )}

                        <p className="text-xs text-white/40 mt-3">
                            Aggregated from confirmed, in-progress and completed bookings on the StringsSG platform, computed {benchmarkAsOf}. Rates are the
                            package price divided by the sessions in the package.
                            {benchmarks.totalSessions > 0 && (
                                <> Those sessions come from{' '}
                                <strong className="text-white/60">{benchmarks.sampleSize.toLocaleString('en-SG')} separately priced packages</strong> — that
                                package count, not the session count, is the sample size behind the medians, since each package carries one negotiated price.</>
                            )}
                            {' '}Breakdown rows with fewer than five packages are not shown, and corporate bookings are excluded because those rates are
                            negotiated separately.
                            {benchmarks.estimatedTotalPackages > 0 && (
                                <> The total package figure quoted above is an operational estimate, not a measurement: a share of our packages are closed
                                manually outside the booking system and carry no pricing record, so they are counted in that total but contribute nothing to the
                                medians. Only the {benchmarks.sampleSize.toLocaleString('en-SG')} priced packages do.</>
                            )}
                            {benchmarks.excludedCount > 0 && ` ${benchmarks.excludedCount} booking${benchmarks.excludedCount === 1 ? ' was' : 's were'} set aside as data errors.`}
                            {' '}Figures describe StringsSG activity and are not a nationally representative survey of Singapore. Journalists and researchers are
                            welcome to cite these figures with attribution.
                        </p>
                    </div>
                )}

                {/* Named-provider comparison. An operator cannot publish this about its rivals. */}
                <div id="provider-rates" className="scroll-mt-20 my-8">
                    <h2 className="text-xl md:text-2xl font-bold text-white mb-3">Personal trainer rates by provider in Singapore</h2>
                    <p className="text-white/70 leading-relaxed mb-4">
                        Most cost guides in Singapore are written by a gym or studio, which is why they compare unnamed categories rather than named providers. We
                        are a marketplace, so we can name them. Every rate below was checked against the provider&apos;s own page on the date shown. Where a provider
                        does not publish a rate, we say so rather than guess.
                    </p>
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse text-sm text-white/60">
                            <thead className="bg-white/6">
                                <tr>
                                    <th className="border border-white/10 p-3 text-left text-orange-400">Provider</th>
                                    <th className="border border-white/10 p-3 text-left text-orange-400">Format</th>
                                    <th className="border border-white/10 p-3 text-left text-orange-400">Published rate</th>
                                    <th className="border border-white/10 p-3 text-left text-orange-400">Membership</th>
                                </tr>
                            </thead>
                            <tbody>
                                {ptMarketRates.map((row, i) => (
                                    <tr key={row.provider} className={i % 2 ? "bg-white/2" : ""}>
                                        <td className="border border-white/10 p-3 align-top">
                                            <a href={row.url} target="_blank" rel="noreferrer" className="text-orange-400/80 hover:text-orange-400 underline">
                                                {row.provider}
                                            </a>
                                        </td>
                                        <td className="border border-white/10 p-3 align-top">{row.format}</td>
                                        <td className={`border border-white/10 p-3 align-top font-semibold ${row.published ? "text-white/80" : "text-white/35"}`}>
                                            {row.rate}
                                        </td>
                                        <td className="border border-white/10 p-3 align-top">{row.membership}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="mt-4 space-y-2">
                        {ptMarketRates.filter((row) => row.note).map((row) => (
                            <p key={row.provider} className="text-xs text-white/40 leading-relaxed">
                                <span className="text-white/60 font-semibold">{row.provider}:</span> {row.note}
                            </p>
                        ))}
                    </div>
                    <p className="text-xs text-white/30 mt-4">
                        All entries last verified {ptMarketRatesVerifiedOn}. Published rates change without notice — check with the provider before budgeting.
                        Spotted a figure that has moved?{' '}
                        <a href={`mailto:${harveyFinn.email}`} className="text-orange-400/70 hover:text-orange-400 underline">Tell us</a> and we will re-verify it.
                    </p>
                </div>

                {/* Monthly framing - the gap against the currently ranking cost guides */}
                <div id="monthly-cost" className="scroll-mt-20 my-8">
                    <h2 className="text-xl md:text-2xl font-bold text-white mb-3">How much does a personal trainer cost per month?</h2>
                    <p className="text-white/70 leading-relaxed mb-4">
                        Per-session rates are how trainers quote, but monthly outlay is how most people actually decide. At the market rates above, here is what
                        each training frequency costs over a month, before any package discount.
                    </p>
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse text-sm text-white/60">
                            <thead className="bg-white/6">
                                <tr>
                                    <th className="border border-white/10 p-3 text-left text-orange-400">Trainer tier</th>
                                    <th className="border border-white/10 p-3 text-right text-orange-400">Per session</th>
                                    <th className="border border-white/10 p-3 text-right text-orange-400">1x / week</th>
                                    <th className="border border-white/10 p-3 text-right text-orange-400">2x / week</th>
                                    <th className="border border-white/10 p-3 text-right text-orange-400">3x / week</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className="border border-white/10 p-3">Newly certified</td>
                                    <td className="border border-white/10 p-3 text-right">$70 - $90</td>
                                    <td className="border border-white/10 p-3 text-right">$300 - $390</td>
                                    <td className="border border-white/10 p-3 text-right font-semibold text-white/80">$610 - $780</td>
                                    <td className="border border-white/10 p-3 text-right">$910 - $1,170</td>
                                </tr>
                                <tr className="bg-white/2">
                                    <td className="border border-white/10 p-3">Experienced freelance</td>
                                    <td className="border border-white/10 p-3 text-right">$100 - $150</td>
                                    <td className="border border-white/10 p-3 text-right">$430 - $650</td>
                                    <td className="border border-white/10 p-3 text-right font-semibold text-white/80">$870 - $1,300</td>
                                    <td className="border border-white/10 p-3 text-right">$1,300 - $1,950</td>
                                </tr>
                                <tr>
                                    <td className="border border-white/10 p-3">Commercial gym trainer</td>
                                    <td className="border border-white/10 p-3 text-right">$140 - $200</td>
                                    <td className="border border-white/10 p-3 text-right">$610 - $870</td>
                                    <td className="border border-white/10 p-3 text-right font-semibold text-white/80">$1,210 - $1,730</td>
                                    <td className="border border-white/10 p-3 text-right">$1,820 - $2,600</td>
                                </tr>
                                <tr className="bg-white/2">
                                    <td className="border border-white/10 p-3">Elite or specialist</td>
                                    <td className="border border-white/10 p-3 text-right">$180 - $250</td>
                                    <td className="border border-white/10 p-3 text-right">$780 - $1,080</td>
                                    <td className="border border-white/10 p-3 text-right font-semibold text-white/80">$1,560 - $2,170</td>
                                    <td className="border border-white/10 p-3 text-right">$2,340 - $3,250</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <p className="text-xs text-white/40 mt-3">
                        Based on 4.33 sessions per month for weekly training. Commercial gym figures exclude the membership fee, which is charged separately and
                        typically adds $150 to $250 a month.
                    </p>
                </div>

                {/* Interactive Calculator first for engagement */}
                <div id="cost-calculator" className="scroll-mt-20">
                    <PtCostCalculator/>
                </div>
                {/* Total Cost of Ownership Comparison */}
                <div className="relative overflow-hidden rounded-2xl py-6 px-3 my-8 bg-white/4 backdrop-blur-xl border border-blue-400/20 shadow-xl shadow-blue-500/10">
                    <div className="absolute inset-x-0 top-0 h-px bg-linear-to-r from-transparent via-blue-400/30 to-transparent" />
                    <div className="absolute -top-20 -right-20 w-40 h-40 rounded-full bg-blue-500/5 blur-3xl pointer-events-none" />
                    <div className="relative z-10">
                    <h2 className="text-center font-bold text-xl text-blue-400 mb-4">
                        Total cost comparison: commercial gym vs independent trainer
                    </h2>
                    <p className="text-white/50 mb-4 text-center">
                        Most articles only show you per-session rates. Here&apos;s the <strong className="text-white/70">REAL total cost</strong> for training twice a week for a year:
                    </p>
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse text-sm text-white/60">
                            <thead className="bg-white/6">
                                <tr>
                                    <th className="border border-white/10 p-3 text-left text-blue-400">Cost Component</th>
                                    <th className="border border-white/10 p-3 text-center text-blue-400">Commercial Gym PT</th>
                                    <th className="border border-white/10 p-3 text-center text-blue-400">Freelance PT (Condo/Outdoor)</th>
                                    <th className="border border-white/10 p-3 text-center text-blue-400">Freelance PT (ActiveSG)</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className="border border-white/10 p-3">Gym Membership (Annual)</td>
                                    <td className="border border-white/10 p-3 text-center text-red-400 font-semibold">$1,800 - $3,000</td>
                                    <td className="border border-white/10 p-3 text-center text-emerald-400 font-semibold">$0</td>
                                    <td className="border border-white/10 p-3 text-center text-emerald-400 font-semibold">$300</td>
                                </tr>
                                <tr className="bg-white/2">
                                    <td className="border border-white/10 p-3">PT Sessions (96 sessions/year)</td>
                                    <td className="border border-white/10 p-3 text-center">$11,520 - $19,200</td>
                                    <td className="border border-white/10 p-3 text-center">$6,720 - $13,440</td>
                                    <td className="border border-white/10 p-3 text-center">$6,720 - $13,440</td>
                                </tr>
                                <tr>
                                    <td className="border border-white/10 p-3">Sign-up/Admin Fees</td>
                                    <td className="border border-white/10 p-3 text-center">$100 - $500</td>
                                    <td className="border border-white/10 p-3 text-center">$0</td>
                                    <td className="border border-white/10 p-3 text-center">$0</td>
                                </tr>
                                <tr className="bg-white/4 font-bold">
                                    <td className="border border-white/10 p-3 text-white/80">TOTAL ANNUAL COST</td>
                                    <td className="border border-white/10 p-3 text-center text-red-400">$11,500 - $20,780</td>
                                    <td className="border border-white/10 p-3 text-center text-emerald-400">$6,720 - $13,440</td>
                                    <td className="border border-white/10 p-3 text-center text-emerald-400">$7,020 - $13,740</td>
                                </tr>
                                <tr className="bg-emerald-500/6">
                                    <td className="border border-white/10 p-3 font-semibold text-white/80">’° Your Savings vs Gym PT</td>
                                    <td className="border border-white/10 p-3 text-center"></td>
                                    <td className="border border-white/10 p-3 text-center text-emerald-400 font-bold">$4,780 - $7,340</td>
                                    <td className="border border-white/10 p-3 text-center text-emerald-400 font-bold">$4,480 - $7,040</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <p className="text-sm text-white/30 mt-4 text-center">
                        * Calculations based on average market rates. Commercial gym PT: $120-$180/session. Freelance PT: $70-$120/session.
                    </p>
                    </div>
                </div>
            </div>
            {/* Neutral gym vs independent comparison (replaces the previous promotional block) */}
            <section className="max-w-5xl mx-auto py-8 px-4">
                <h2 className="text-xl md:text-2xl font-bold text-white mb-3">Commercial gym vs independent personal trainer</h2>
                <p className="text-white/60 mb-4 text-sm leading-relaxed">
                    The per-session rate is only part of what you pay. Commercial gyms usually require a paid membership alongside your training package, which
                    changes the total cost considerably.
                </p>
                <div className="overflow-x-auto">
                    <table className="w-full border-collapse text-sm text-white/60">
                        <thead className="bg-white/6">
                            <tr>
                                <th className="border border-white/10 p-3 text-left text-orange-400">Option</th>
                                <th className="border border-white/10 p-3 text-right text-orange-400">Per session</th>
                                <th className="border border-white/10 p-3 text-left text-orange-400">Membership</th>
                                <th className="border border-white/10 p-3 text-left text-orange-400">Best for</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td className="border border-white/10 p-3">Commercial gym trainer</td>
                                <td className="border border-white/10 p-3 text-right font-semibold text-white/80">$140 - $200</td>
                                <td className="border border-white/10 p-3">$100 - $250/month</td>
                                <td className="border border-white/10 p-3">Full facilities and classes</td>
                            </tr>
                            <tr className="bg-white/2">
                                <td className="border border-white/10 p-3">Independent trainer</td>
                                <td className="border border-white/10 p-3 text-right font-semibold text-white/80">$80 - $150</td>
                                <td className="border border-white/10 p-3">Not required</td>
                                <td className="border border-white/10 p-3">Flexibility on time and place</td>
                            </tr>
                            <tr>
                                <td className="border border-white/10 p-3">Home or condo gym</td>
                                <td className="border border-white/10 p-3 text-right font-semibold text-white/80">$100 - $140</td>
                                <td className="border border-white/10 p-3">Not required</td>
                                <td className="border border-white/10 p-3">Convenience and privacy</td>
                            </tr>
                            <tr className="bg-white/2">
                                <td className="border border-white/10 p-3">Online training</td>
                                <td className="border border-white/10 p-3 text-right font-semibold text-white/80">$50 - $120</td>
                                <td className="border border-white/10 p-3">No</td>
                                <td className="border border-white/10 p-3">Lower budgets, self-directed</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <p className="text-xs text-white/40 mt-3">
                    Training twice a week for a year works out at roughly $6,700 - $13,400 with an independent trainer, or $11,500 - $20,800 at a commercial gym
                    once membership is included. See{' '}
                    <Link href="/sg/personal-trainer/personal-trainer-cost-statistics-singapore" prefetch={false} className="text-orange-400/80 hover:text-orange-400 underline">
                        personal trainer cost statistics
                    </Link>{' '}for how this varies across the market.
                </p>
            </section>



            <section className="max-w-5xl mx-auto py-12 px-4 space-y-10 text-white/70">
                <header className="text-center space-y-4">
                    <h2 className="text-xl md:text-2xl font-bold uppercase tracking-wide text-white">
                    What determines personal trainer rates in Singapore?
                    </h2>
                </header>

                <article className="space-y-6 text-white/60">
                    <div className="space-y-2">
                    <h3 className="text-lg font-semibold text-white/80">
                        1. Gym trainers vs. freelance personal trainers
                    </h3>
                    <p>
                        Hiring a trainer through a commercial gym often costs more than working with a freelance personal trainer. Gyms typically take a cut from the fees. However, they may also offer perks such as premium equipment, steam rooms, saunas, and supervised program audits.
                    </p>
                    <h4 className="font-medium text-md text-white/80">Facilities Provided:</h4>
                    <ul className="list-disc pl-5 space-y-1">
                        <li><strong>Gym Trainers:</strong> Access to fully equipped facilities including pools, saunas, steam rooms, and group fitness classes.</li>
                        <li><strong>Freelancers:</strong> Training can occur in various locations, but additional fees may apply if equipment is needed or if sessions are at premium venues.</li>
                    </ul>
                    <h4 className="font-medium text-md text-white/80">Equipment Provided:</h4>
                    <ul className="list-disc pl-5 space-y-1">
                        <li><strong>Gym Trainers:</strong> Professional-grade equipment such as Olympic weight racks, cardio machines (treadmills, ellipticals, exercise bikes), and free weights.</li>
                        <li><strong>Freelancers:</strong> May bring portable equipment like dumbbells or resistance bands; otherwise, rely on bodyweight exercises or client&apos;s home gym setup.</li>
                    </ul>
                    </div>

                    <div className="space-y-2">
                    <h3 className="text-lg font-semibold text-white/80">
                        2. How your fitness goals affect pricing
                    </h3>
                    <p>
                        Most personal training in Singapore falls between $80 and $150 per session, depending on your goals, the trainer&apos;s experience, and the training location. Some packages also include meal planning and nutrition guidance, which can increase the cost.
                    </p>
                    </div>

                    {/** Goal Tiers */}
                    {[
                    {
                        title: "Basic Goals ($80 - $100)",
                        content:
                        "If your goal is to improve general fitness, lose some weight, or enhance cardiovascular health, you may only need simple guidance on bodyweight and functional training. This entry-level support is more budget-friendly.",
                    },
                    {
                        title: "Average Goals ($100 - $120)",
                        content:
 "To build muscle, achieve a V-shaped physique, or improve toning, youll need a trainer who can tailor strength and cardio routines. These intermediate programs demand more expertise and planning.",
                    },
                    {
                        title: "Advanced Goals ($120 - $180)",
                        content: (
                        <>
                            <p>
 Advanced personal training is for serious transformation whether its improving your health significantly or preparing for a competition. This level involves careful supervision, often paired with meal planning and health monitoring.
                            </p>
                            <p className="pl-4 pt-2">
                            <span className="font-medium">a. Obese & over 40: </span>Your trainer may need to review your medical reports and adjust your routine to prevent overstraining your heart or joints.
                            </p>
                            <p className="pl-4 pt-2">
                            <span className="font-medium">b. Stage/photo prep: </span>For fitness models or actors, trainers work closely with dietitians to create strict workout and diet plans based on macronutrient manipulation.
                            </p>
                        </>
                        ),
                    },
                    ].map(({ title, content }, i) => (
                    <div key={i} className="md:grid md:grid-cols-4 border border-white/10 rounded-lg overflow-hidden">
                        <div className="md:col-span-1 p-3 text-orange-400 font-medium bg-white/5 flex items-center justify-center text-center">
                        {title}
                        </div>
                        <div className="md:col-span-3 p-4 text-sm text-white/60">{content}</div>
                    </div>
                    ))}

                    <div className="space-y-2">
                    <h3 className="text-lg font-semibold text-white/80">
                        3. Your experience level + your goal
                    </h3>
                    <p>
                        A beginner working toward basic goals will likely spend less. As your goals become more ambitious, and your baseline fitness increases, you&apos;ll need more personalized and intensive sessions which drives up costs.
                    </p>
                    </div>
                </article>

                <div className="space-y-4">
                    <h3 className="text-center font-semibold text-sm underline uppercase text-white/80">
                    Rate Chart: Expertise vs. Fitness Goals
                    </h3>
                    <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-white/10 text-xs md:text-sm">
                        <thead className="bg-linear-to-r from-orange-500 to-amber-500 text-white">
                        <tr>
                            <th className="border border-white/20 py-2 text-left px-2">Goal</th>
                            <th className="border border-white/20 py-2 px-2">Beginner</th>
                            <th className="border border-white/20 py-2 px-2">Intermediate</th>
                            <th className="border border-white/20 py-2 px-2">Advanced</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr className="text-white/60">
                            <td className="border border-white/10 px-2 py-1 font-semibold text-orange-400">
                            Basic
                            </td>
                            <td className="border border-white/10 px-2 py-1 text-center">$80 - $100</td>
                            <td className="border border-white/10 px-2 py-1 text-center">N/A</td>
                            <td className="border border-white/10 px-2 py-1 text-center">N/A</td>
                        </tr>
                        <tr className="text-white/60 bg-white/2">
                            <td className="border border-white/10 px-2 py-1 font-semibold text-orange-400">
                            Average
                            </td>
                            <td className="border border-white/10 px-2 py-1 text-center">$90 - $120</td>
                            <td className="border border-white/10 px-2 py-1 text-center">$90 - $120</td>
                            <td className="border border-white/10 px-2 py-1 text-center">N/A</td>
                        </tr>
                        <tr className="text-white/60">
                            <td className="border border-white/10 px-2 py-1 font-semibold text-orange-400">
                            Advanced
                            </td>
                            <td className="border border-white/10 px-2 py-1 text-center">$120 - $180</td>
                            <td className="border border-white/10 px-2 py-1 text-center">$120 - $180</td>
                            <td className="border border-white/10 px-2 py-1 text-center">$120 - $180</td>
                        </tr>
                        </tbody>
                    </table>
                    </div>
                </div>

                <div className="bg-white/4 backdrop-blur-xl border border-orange-400/20 rounded-2xl py-6 px-2 shadow-xl shadow-orange-500/10">
                    <h2 className="text-center font-bold text-xl text-orange-400 mb-4">
                        Personal trainer rates by Singapore district
                    </h2>
                    <p className="text-white/60 mb-4">
                        Trainers working in central districts generally charge more, reflecting higher demand and the cost of training space in those areas. Rates in residential and heartland estates are typically lower.
                    </p>

                    {/* No per-district table here, deliberately. Medians over 7-12 packages have
                        confidence intervals wide enough that the district-to-district spread is
                        noise rather than a real geographic effect - and the hand-written ranges
                        this section used to carry had no source at all. The qualitative point
                        above is what we can actually support. Revisit when a district clears
                        roughly 30 packages; the endpoint already returns byDistrict, it is simply
                        not rendered. */}
                    <p className="text-white/50 text-sm">
                        We are not publishing per-district rate figures. Our own booking data does not yet cover enough packages in any single district to quote
                        a median that would hold up, and we would rather say so than print a number that looks precise but is not. For what trainers charge
                        across the island, use the ranges and the provider comparison above.
                    </p>
                </div>

                <div id="by-goal" className="scroll-mt-20 bg-white/4 backdrop-blur-xl border border-green-400/20 rounded-2xl py-6 px-2 shadow-xl shadow-green-500/10">
                    <h2 className="text-center font-bold text-xl text-green-400 mb-4">
                        Personal trainer rates by training goal
                    </h2>
                    <p className="text-white/60 mb-4">
                        Singaporeans pursue diverse fitness journeys, from resolving sedentary lifestyles to preparing for sports events. Programs often incorporate local preferences like resistance to humidity-induced fatigue or quick HIIT for busy urbanites.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        <div className="bg-white/5 p-4 rounded-lg border border-white/8 border-l-4 border-l-green-500">
                            <h4 className="font-semibold text-green-400 flex items-center">
                                Weight Loss
                            </h4>
                            <p className="text-2xl font-bold text-white/90">$100 - $120</p>
                            <p className="text-sm text-white/50">Structured cardio and resistance work paired with practical nutrition guidance for sustainable fat loss.</p>
                        </div>
                        <div className="bg-white/5 p-4 rounded-lg border border-white/8 border-l-4 border-l-green-500">
                            <h4 className="font-semibold text-green-400 flex items-center">
                                Strength & Muscle Building
                            </h4>
                            <p className="text-2xl font-bold text-white/90">$120 - $160</p>
                            <p className="text-sm text-white/50">Combines hypertrophy with functional strength. Ideal for competitive athletes and physique models.</p>
                        </div>
                        <div className="bg-white/5 p-4 rounded-lg border border-white/8 border-l-4 border-l-green-500">
                            <h4 className="font-semibold text-green-400 flex items-center">
                                Flexibility & Mobility
                            </h4>
                            <p className="text-2xl font-bold text-white/90">$90 - $110</p>
                            <p className="text-sm text-white/50">Yoga and mobility work to combat desk jobs. Growing popularity among mid-career office workers.</p>
                        </div>
                        <div className="bg-white/5 p-4 rounded-lg border border-white/8 border-l-4 border-l-green-500">
                            <h4 className="font-semibold text-green-400 flex items-center">
                                Corporate Training
                            </h4>
                            <p className="text-2xl font-bold text-white/90">$100 - $150</p>
                            <p className="text-sm text-white/50">Group sessions for office wellness programs. Includes team-building elements in modern CBD settings.</p>
                        </div>
                        <div className="bg-white/5 p-4 rounded-lg border border-white/8 border-l-4 border-l-green-500">
                            <h4 className="font-semibold text-green-400 flex items-center">
                                Elderly Training
                            </h4>
                            <p className="text-2xl font-bold text-white/90">$90 - $110</p>
                            <p className="text-sm text-white/50">Gentle programs for seniors. Focus on bone health and mobility, often in community centers.</p>
                        </div>
                        <div className="bg-white/5 p-4 rounded-lg border border-white/8 border-l-4 border-l-green-500">
                            <h4 className="font-semibold text-green-400 flex items-center">
                                Pre & Post Natal Training
                            </h4>
                            <p className="text-2xl font-bold text-white/90">$110 - $140</p>
                            <p className="text-sm text-white/50">Safe exercises for pregnancy and recovery. Specialized for new moms in family-friendly environments.</p>
                        </div>
                    </div>
                    <p className="text-sm text-white/40 mt-4">
                        ’ Most programmes include basic nutrition guidance. Confirm what is covered before you commit, as detailed meal planning is often charged separately.
                    </p>
                </div>

                <div id="by-experience" className="scroll-mt-20 bg-white/4 backdrop-blur-xl border border-purple-400/20 rounded-2xl py-6 px-2 shadow-xl shadow-purple-500/10">
                    <h2 className="text-center font-bold text-xl text-purple-400 mb-4">
                        Personal trainer rates by experience and certification
                    </h2>
                    <p className="text-white/60 mb-4">
                        Experience and certification are the single biggest drivers of what a trainer charges. Most established trainers in Singapore hold internationally recognised certifications, and specialist qualifications command higher rates.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="bg-white/5 p-4 rounded-lg border border-white/8 border-t-4 border-t-purple-500 text-center">
                            <div className="text-4xl mb-2"></div>
                            <h4 className="font-semibold text-purple-400">Beginner (0-2 years)</h4>
                            <p className="text-sm text-white/50 mb-2">Entry-level certs (ACE, ISSA)</p>
                            <p className="text-2xl font-bold text-white/90">$80 - $100</p>
                            <p className="text-xs text-white/40">Suitable for newcomers to fitness, focusing on foundational guidance.</p>
                        </div>
                        <div className="bg-white/5 p-4 rounded-lg border border-white/8 border-t-4 border-t-purple-500 text-center">
                            <div className="text-4xl mb-2"></div>
                            <h4 className="font-semibold text-purple-400">Intermediate (2-5 years)</h4>
                            <p className="text-sm text-white/50 mb-2">ACE, NASM, LesMills</p>
                            <p className="text-2xl font-bold text-white/90">$100 - $140</p>
                            <p className="text-xs text-white/40">Experienced with various programs, ideal for progressive training.</p>
                        </div>
                        <div className="bg-white/5 p-4 rounded-lg border border-white/8 border-t-4 border-t-purple-500 text-center">
                            <div className="text-4xl mb-2"></div>
                            <h4 className="font-semibold text-purple-400">Advanced (5+ years)</h4>
                            <p className="text-sm text-white/50 mb-2">NSCA, specialty certs</p>
                            <p className="text-2xl font-bold text-white/90">$140 - $180</p>
                            <p className="text-xs text-white/40">Elite trainers with track records, handling complex fitness goals.</p>
                        </div>
                    </div>
                    <p className="text-sm text-white/40 mt-4">
                        ’ In Singapore, certifications from bodies like Republic Polytechnic or NUS recognized programs add credibility and justify premium rates.
                    </p>
                </div>

                <div id="by-location" className="scroll-mt-20 bg-white/4 backdrop-blur-xl border border-cyan-400/20 rounded-2xl py-6 px-2 shadow-xl shadow-cyan-500/10">
                    <h2 className="text-center font-bold text-xl text-cyan-400 mb-4">
                        Personal trainer cost by training location
                    </h2>
                    <p className="text-white/60 mb-6">
                        Where you train affects both price and convenience. Condo and home sessions avoid a gym membership, community facilities are the most affordable, and commercial gyms cost the most once membership is included.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-white/5 p-4 rounded-lg border border-white/8 border-l-4 border-l-cyan-500">
                            <h4 className="font-semibold text-cyan-400 flex items-center mb-2">
                                Online Training
                            </h4>
                            <p className="text-sm text-white/50 mb-2">Virtual sessions via Zoom or mobile apps, perfect for early risers and late workers.</p>
                            <p className="text-lg font-bold text-white/90">$50 - $120</p>
                            <p className="text-xs text-white/40">($10-$20 less than in-person; includes app-based form checks)</p>
                        </div>
                        <div className="bg-white/5 p-4 rounded-lg border border-white/8 border-l-4 border-l-cyan-500">
                            <h4 className="font-semibold text-cyan-400 flex items-center mb-2">
                                Condo Gym
                            </h4>
                            <p className="text-sm text-white/50 mb-2">Private facilities in HDB towers, limited to residents. Convenient for families.</p>
                            <p className="text-lg font-bold text-white/90">$100 - $140</p>
                            <p className="text-xs text-white/40">(Access via residents; basic to premium equipment available)</p>
                        </div>
                        <div className="bg-white/5 p-4 rounded-lg border border-white/8 border-l-4 border-l-cyan-500">
                            <h4 className="font-semibold text-cyan-400 flex items-center mb-2">
                                Home Training
                            </h4>
                            <p className="text-sm text-white/50 mb-2">Trainer visits your home (condo or landed), fully customized. Popular post-lockdown.</p>
                            <p className="text-lg font-bold text-white/90">$100 - $140</p>
                            <p className="text-xs text-white/40">(Includes travel time; privacy for sensitive goals like post-surgery rehab)</p>
                        </div>
                        <div className="bg-white/5 p-4 rounded-lg border border-white/8 border-l-4 border-l-cyan-500">
                            <h4 className="font-semibold text-cyan-400 flex items-center mb-2">
                                Community Gym
                            </h4>
                            <p className="text-sm text-white/50 mb-2">ActiveSG gyms and community facilities. The most affordable way to train with full equipment access.</p>
                            <p className="text-lg font-bold text-white/90">$110 - $150</p>
                            <p className="text-xs text-white/40">(Entry booked via the ActiveSG app; low per-entry or annual pass fees instead of a gym membership)</p>
                        </div>
                    </div>
                    <p className="text-sm text-white/40 mt-4">
                        ’ Choose based on your lifestyle many Singaporeans mix online for weekdays and in-person for weekends.
                    </p>
                </div>

                <div className="bg-white/4 backdrop-blur-xl border border-indigo-400/20 rounded-2xl py-5 px-4 shadow-xl shadow-indigo-500/10">
                    <h3 className="font-bold text-lg text-indigo-400 mb-2">Session timing</h3>
                    <p className="text-white/60 text-sm leading-relaxed">
                        Demand concentrates before and after office hours, so those slots carry a premium of roughly $10 to $20 per session. Midday sessions
                        (10am to 4pm) are easier to book and usually priced at the standard rate. Weekends typically run 10-20% higher because availability is limited.
                    </p>
                </div>
            </section>

            {/* Package Pricing Breakdown Section */}
            <section className="max-w-5xl mx-auto py-12 px-2 space-y-8">
                <div className="bg-white/4 backdrop-blur-xl border border-emerald-400/20 rounded-2xl py-6 px-2 shadow-xl shadow-emerald-500/10">
                    <h2 className="text-center font-bold text-2xl text-emerald-400 mb-4">
                         Personal Training Package Pricing Breakdown
                    </h2>
                    <p className="text-white/60 mb-6 text-center">
                        Most trainers offer discounts for bulk purchases. Here&apos;s what you can expect to pay for different package sizes:
                    </p>
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse text-sm text-white/60">
                            <thead className="bg-white/6">
                                <tr>
                                    <th className="border border-white/10 p-3 text-left text-emerald-400">Package Size</th>
                                    <th className="border border-white/10 p-3 text-center text-emerald-400">Freelance PT</th>
                                    <th className="border border-white/10 p-3 text-center text-emerald-400">Gym PT</th>
                                    <th className="border border-white/10 p-3 text-center text-emerald-400">Typical Discount</th>
                                    <th className="border border-white/10 p-3 text-center text-emerald-400">Best For</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className="border border-white/10 p-3 font-semibold text-white/80">Single Session (Trial)</td>
                                    <td className="border border-white/10 p-3 text-center">$60 - $120</td>
                                    <td className="border border-white/10 p-3 text-center">$90 - $180</td>
                                    <td className="border border-white/10 p-3 text-center">None</td>
                                    <td className="border border-white/10 p-3 text-center text-xs">Testing chemistry</td>
                                </tr>
                                <tr className="bg-white/2">
                                    <td className="border border-white/10 p-3 font-semibold text-white/80">10 Sessions</td>
                                    <td className="border border-white/10 p-3 text-center">$1,000 - $1,450</td>
                                    <td className="border border-white/10 p-3 text-center">$1,200 - $1,600</td>
                                    <td className="border border-white/10 p-3 text-center text-emerald-400">5-10% off</td>
                                    <td className="border border-white/10 p-3 text-center text-xs">Beginners (1-2 months)</td>
                                </tr>
                                <tr>
                                    <td className="border border-white/10 p-3 font-semibold text-white/80">20 Sessions</td>
                                    <td className="border border-white/10 p-3 text-center">$2,000 - $2,900</td>
                                    <td className="border border-white/10 p-3 text-center">$2,600 - $3,200</td>
                                    <td className="border border-white/10 p-3 text-center text-emerald-400">10-15% off</td>
                                    <td className="border border-white/10 p-3 text-center text-xs">Committed (2-3 months)</td>
                                </tr>
                                <tr className="bg-white/2">
                                    <td className="border border-white/10 p-3 font-semibold text-white/80">30 Sessions</td>
                                    <td className="border border-white/10 p-3 text-center">$3,400 - $3,900</td>
                                    <td className="border border-white/10 p-3 text-center">$3,800 - $4,200</td>
                                    <td className="border border-white/10 p-3 text-center text-emerald-400">15-20% off</td>
                                    <td className="border border-white/10 p-3 text-center text-xs">Serious transformation</td>
                                </tr>
                                <tr className="bg-emerald-500/6">
                                    <td className="border border-white/10 p-3 font-semibold text-white/80">50+ Sessions</td>
                                    <td className="border border-white/10 p-3 text-center">$5,600 - $6,200</td>
                                    <td className="border border-white/10 p-3 text-center">$6,100 - $7,300</td>
                                    <td className="border border-white/10 p-3 text-center text-emerald-400 font-bold">20-25% off</td>
                                    <td className="border border-white/10 p-3 text-center text-xs">Long-term commitment</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div className="mt-4 bg-amber-500/10 p-4 rounded-lg border-l-4 border-amber-500">
                        <p className="text-sm text-white/60">
                            <strong>’ Pro Tip:</strong> Start with a 10-session package to evaluate your trainer. Don&apos;t commit to 50+ sessions upfront many gyms make it difficult to get refunds if you&apos;re unhappy. Good freelance trainers will let you pay-as-you-go after the initial package.
                        </p>
                    </div>
                </div>

                {/* 1-on-1 vs Buddy vs Group Training */}
                <div className="bg-white/4 backdrop-blur-xl border border-violet-400/20 rounded-2xl py-6 px-2 shadow-xl shadow-violet-500/10">
                    <h2 className="text-center font-bold text-2xl text-violet-400 mb-4">
                         Training Types: 1-on-1 vs Buddy vs Group
                    </h2>
                    <p className="text-white/60 mb-6 text-center">
                        Choosing the right training format can significantly impact both your results and your wallet.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="bg-white/5 p-5 rounded-lg border border-white/8 border-t-4 border-t-violet-500">
                            <h3 className="font-bold text-violet-400 text-lg mb-2 flex items-center">
                                1-on-1 Training
                            </h3>
                            <p className="text-2xl font-bold text-white/90 mb-2">$90 - $180/session</p>
                            <div className="space-y-2 text-sm">
                                <p className="text-green-400">100% personalized attention</p>
                                <p className="text-green-400">Fastest results</p>
                                <p className="text-green-400">Flexible scheduling</p>
                                <p className="text-green-400">Private &amp; comfortable</p>
 <p className="text-red-400">Most expensive option</p>
                            </div>
                            <p className="text-xs text-white/40 mt-3">Best for: Beginners, specific goals, injury rehab</p>
                        </div>
                        <div className="bg-white/5 p-5 rounded-lg border border-white/8 border-t-4 border-t-violet-500">
                            <h3 className="font-bold text-violet-400 text-lg mb-2 flex items-center">
                                Buddy Training (2 pax)
                            </h3>
                            <p className="text-2xl font-bold text-white/90 mb-2">$65 - $90/person</p>
                            <div className="space-y-2 text-sm">
                                <p className="text-green-400">30-40% cheaper per person</p>
                                <p className="text-green-400">Built-in accountability</p>
                                <p className="text-green-400">More fun &amp; motivating</p>
 <p className="text-red-400">Scheduling coordination</p>
 <p className="text-red-400">Less personalized</p>
                            </div>
                            <p className="text-xs text-white/40 mt-3">Best for: Couples, friends, similar fitness levels</p>
                        </div>
                        <div className="bg-white/5 p-5 rounded-lg border border-white/8 border-t-4 border-t-violet-500">
                            <h3 className="font-bold text-violet-400 text-lg mb-2 flex items-center">
                                Group Training (4-8 pax)
                            </h3>
                            <p className="text-2xl font-bold text-white/90 mb-2">$40 - $60/person</p>
                            <div className="space-y-2 text-sm">
                                <p className="text-green-400">Most affordable</p>
                                <p className="text-green-400">Social &amp; energetic</p>
                                <p className="text-green-400">Community building</p>
 <p className="text-red-400">Generic programming</p>
 <p className="text-red-400">Limited individual feedback</p>
                            </div>
                            <p className="text-xs text-white/40 mt-3">Best for: General fitness, weight loss, socializing</p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="max-w-5xl mx-auto py-12 px-4 space-y-8">
                {/* How to get a better rate */}
                <div className="bg-white/4 backdrop-blur-xl border border-lime-400/20 rounded-2xl py-6 px-4 shadow-xl shadow-lime-500/10">
                    <h2 className="text-center font-bold text-xl text-lime-400 mb-4">
                        How to get a better rate
                    </h2>
                    <p className="text-white/60 text-sm mb-4">
                        Most independent trainers have some flexibility on price. The levers that actually work are the ones that make you a more valuable client:
                    </p>
                    <ul className="space-y-2 text-sm text-white/60 mb-4">
                        <li> <strong className="text-white/80">Commit to a larger package.</strong> Buying 20 or 30 sessions upfront usually lowers the per-session rate.</li>
                        <li> <strong className="text-white/80">Train off-peak.</strong> Weekday mornings and midday slots are easier for a trainer to fill.</li>
                        <li> <strong className="text-white/80">Train with a partner.</strong> Buddy training cuts the cost per person by roughly 30%.</li>
                        <li> <strong className="text-white/80">Train near where the trainer already works.</strong> Less travel time is worth real money to them.</li>
                        <li> <strong className="text-white/80">Agree the rate before your first session,</strong> not after you have started.</li>
                    </ul>
                    <div className="bg-green-500/10 p-4 rounded-lg border-l-4 border-green-500">
                        <p className="text-sm text-white/60">
                            <strong>How to ask:</strong> &quot;I want to commit to 20 sessions and I can train on weekday mornings. Is there a package rate that
                            works better than standard pricing?&quot;
                        </p>
                    </div>
                </div>

            </section>

            <section className="max-w-5xl mx-auto py-12 px-4 space-y-6">
                <div className="bg-amber-500/10 p-4 rounded-lg border-l-4 border-amber-500">
                    <p className="text-sm text-white/60">
                        <strong>Before you book:</strong> Personal trainers in Singapore are not government-licensed, so verifying credentials is up to you. Ask to
                        see a current certification from a recognised body such as ACE, NASM, NSCA or ISSA, and get the full price and what it includes in writing.{' '}
                        <Link href="/sg/personal-trainer/choosing-a-personal-trainer-singapore" prefetch={false} className="text-orange-400 hover:text-orange-300 underline">
                            Read our full guide to choosing a personal trainer
                        </Link>.
                    </p>
                </div>
            </section>

            {/* StringsSG's own pricing - built from real package data */}
            <section id="stringssg-prices" className="scroll-mt-20 max-w-5xl mx-auto px-4 py-10">
                <h2 className="text-xl md:text-2xl font-bold text-white mb-3">How much does a StringsSG personal trainer cost?</h2>
                <p className="text-white/60 mb-5 text-sm leading-relaxed">
                    Our per-session rate falls as the package size increases. Prices below are for 1-on-1 training and include programme design, nutrition
                    guidance and progress tracking. Buddy training is charged at 70% of the solo rate per person.
                </p>
                <div className="overflow-x-auto">
                    <table className="w-full border-collapse text-sm text-white/60">
                        <thead className="bg-white/6">
                            <tr>
                                <th className="border border-white/10 p-3 text-left text-orange-400">Package</th>
                                <th className="border border-white/10 p-3 text-right text-orange-400">Per session (from)</th>
                                <th className="border border-white/10 p-3 text-right text-orange-400">Buddy, per person</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td className="border border-white/10 p-3">14 sessions</td>
                                <td className="border border-white/10 p-3 text-right font-semibold text-white/80">$104</td>
                                <td className="border border-white/10 p-3 text-right">$73</td>
                            </tr>
                            <tr className="bg-white/2">
                                <td className="border border-white/10 p-3">24 sessions</td>
                                <td className="border border-white/10 p-3 text-right font-semibold text-white/80">$99</td>
                                <td className="border border-white/10 p-3 text-right">$69</td>
                            </tr>
                            <tr>
                                <td className="border border-white/10 p-3">30 sessions</td>
                                <td className="border border-white/10 p-3 text-right font-semibold text-white/80">$95</td>
                                <td className="border border-white/10 p-3 text-right">$67</td>
                            </tr>
                            <tr className="bg-white/2">
                                <td className="border border-white/10 p-3">50 sessions</td>
                                <td className="border border-white/10 p-3 text-right font-semibold text-white/80">$90</td>
                                <td className="border border-white/10 p-3 text-right">$63</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <p className="text-xs text-white/40 mt-3">
                    Rates vary by trainer tier.{' '}
                    <Link href="/sg/personal-trainer/personal-training-price-list" prefetch={false} className="text-orange-400/80 hover:text-orange-400 underline">
                        See the full StringsSG package price list
                    </Link>.
                </p>
            </section>

            {/* Pricing FAQs - this page is the canonical owner of pricing-intent queries */}
            <section id="faqs" className="scroll-mt-20 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-72 h-72 bg-orange-500/8 rounded-full blur-3xl" />
                <div className="relative z-10 max-w-5xl mx-auto px-4">
                    <DarkFaq
                        title="Personal Trainer Rates FAQs"
                        subtitle="Common questions about how much personal training costs in Singapore"
                        questionsAnswers={ptRatesFaqs}
                    />
                    {/* Author bio and correction contact sit after the content, not above it */}
                    {/* <ArticleByline author={harveyFinn} variant="footer" /> */}
                </div>
            </section>

            {/* Internal links out to supporting pages */}
            <section className="max-w-5xl mx-auto px-4 py-10">
                <h2 className="text-xl md:text-2xl font-bold text-white text-center mb-6">
                    Explore Personal Training Options in Singapore
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <Link href="/sg/personal-trainer/1-on-1-personal-training" prefetch={false} className="block bg-white/5 hover:bg-white/8 border border-white/10 rounded-xl p-4 transition-colors">
                        <h3 className="font-semibold text-orange-400">1-on-1 Personal Training</h3>
                        <p className="text-sm text-white/50 mt-1">Fully individual coaching built around your goals.</p>
                    </Link>
                    <Link href="/sg/personal-trainer/buddy-training" prefetch={false} className="block bg-white/5 hover:bg-white/8 border border-white/10 rounded-xl p-4 transition-colors">
                        <h3 className="font-semibold text-orange-400">Buddy Training</h3>
                        <p className="text-sm text-white/50 mt-1">Train with a partner and lower your cost per person.</p>
                    </Link>
                    <Link href="/sg/personal-trainer/packages" prefetch={false} className="block bg-white/5 hover:bg-white/8 border border-white/10 rounded-xl p-4 transition-colors">
                        <h3 className="font-semibold text-orange-400">Personal Training Packages</h3>
                        <p className="text-sm text-white/50 mt-1">Compare StringsSG package pricing and inclusions.</p>
                    </Link>
                    <Link href="/sg/personal-trainer/private-home-trainer" prefetch={false} className="block bg-white/5 hover:bg-white/8 border border-white/10 rounded-xl p-4 transition-colors">
                        <h3 className="font-semibold text-orange-400">Home &amp; Condo Gym Training</h3>
                        <p className="text-sm text-white/50 mt-1">Train where you live, with no gym membership needed.</p>
                    </Link>
                    <Link href="/sg/personal-trainer/weight-loss-trainer" prefetch={false} className="block bg-white/5 hover:bg-white/8 border border-white/10 rounded-xl p-4 transition-colors">
                        <h3 className="font-semibold text-orange-400">Weight Loss Training</h3>
                        <p className="text-sm text-white/50 mt-1">Coaching focused on sustainable fat loss.</p>
                    </Link>
                    <Link href="/sg/personal-trainer/body-building-trainer" prefetch={false} className="block bg-white/5 hover:bg-white/8 border border-white/10 rounded-xl p-4 transition-colors">
                        <h3 className="font-semibold text-orange-400">Muscle &amp; Strength Training</h3>
                        <p className="text-sm text-white/50 mt-1">Hypertrophy and strength programmes with a coach.</p>
                    </Link>
                    <Link href="/sg/personal-trainer/choosing-a-personal-trainer-singapore" prefetch={false} className="block bg-white/5 hover:bg-white/8 border border-white/10 rounded-xl p-4 transition-colors">
                        <h3 className="font-semibold text-orange-400">How to Choose a Trainer</h3>
                        <p className="text-sm text-white/50 mt-1">What to check, red flags, and when to switch.</p>
                    </Link>
                    <Link href="/sg/personal-trainer/personal-trainer-cost-statistics-singapore" prefetch={false} className="block bg-white/5 hover:bg-white/8 border border-white/10 rounded-xl p-4 transition-colors">
                        <h3 className="font-semibold text-orange-400">Cost Statistics</h3>
                        <p className="text-sm text-white/50 mt-1">Spend by age group, trainer type and location.</p>
                    </Link>
                </div>
            </section>

            <div className='py-10 text-center'>
                <Link href="/sg/personal-trainer" className='inline-flex items-center gap-2 px-8 py-4 bg-linear-to-r from-orange-500 to-amber-500 text-white font-bold rounded-full shadow-lg shadow-orange-500/20 hover:shadow-xl hover:opacity-90 transition-all duration-300'>
                    Compare certified personal trainers in Singapore
                </Link>
            </div>
            {/* <BlogsList blogs={fitnessBlogs} wpPosts={wpPosts} wpNewPosts={wpNewPosts}/> */}
        </div>
        {isLoading &&
          <LoadingCover/>
        }
        <AnimatePresence>
            {isSystemError && <SystemError errorContent={errorContent} setIsSystemError={setIsSystemError}/>}
        </AnimatePresence>
        <Script
          id="google-tagmanager-new"
          src="https://www.googletagmanager.com/gtag/js?id=G-QD1HGGHDYJ"
          strategy="lazyOnload"
          onLoad={() => {
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());

            gtag('config', 'G-QD1HGGHDYJ');
          }}
      />
      <Script id="facebook-tracking" strategy="lazyOnload">
          {`!function(f,b,e,v,n,t,s)
					  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
					  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
					  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
					  n.queue=[];t=b.createElement(e);t.async=!0;
					  t.src=v;s=b.getElementsByTagName(e)[0];
					  s.parentNode.insertBefore(t,s)}(window, document,'script',
					  'https://connect.facebook.net/en_US/fbevents.js');
					  fbq('init', '128634041116395');
					  fbq('track', 'PageView');`}
        </Script>
    </>
  )
}
